import type { IssueId } from './issue'
import type { UserId } from './project'

const FEEDBACK_STATUS = {
	OPEN: 'open',
	RESOLVED: 'resolved',
	DISMISSED: 'dismissed',
} as const

const FEEDBACK_STATUSES = [FEEDBACK_STATUS.OPEN, FEEDBACK_STATUS.RESOLVED, FEEDBACK_STATUS.DISMISSED] as const

type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number]

const CORRECTION_STATUSES = ['pending', 'accepted', 'dismissed'] as const

type CorrectionStatus = (typeof CORRECTION_STATUSES)[number]

const FEEDBACK_KIND = {
	THREAD: 'thread',
	DOC: 'doc',
} as const

type FeedbackKind = (typeof FEEDBACK_KIND)[keyof typeof FEEDBACK_KIND]

type FeedbackNote = {
	readonly id: string
	readonly author: UserId | undefined
	readonly content: string
	readonly at: Date
}

type FeedbackCorrection = FeedbackNote & {
	readonly status: CorrectionStatus
}

type FeedbackItem = {
	readonly kind: FeedbackKind
	readonly id: string
	readonly host: string
	readonly page: string
	readonly status: FeedbackStatus
	readonly issueId: IssueId | undefined
	readonly author: UserId | undefined
	readonly createdAt: Date
	readonly summary: string
	readonly messages: readonly FeedbackNote[]
	readonly corrections: readonly FeedbackCorrection[]
}

type FeedbackPageGroup = {
	readonly host: string
	readonly page: string
	readonly items: readonly FeedbackItem[]
}

const isLocalHost = (host: string): boolean => host === 'localhost' || /^[\d.]+$/.test(host) || host.includes(':')

const feedbackUrl = (host: string, page: string): string => `${isLocalHost(host) ? 'http' : 'https'}://${host}${page}`

const groupFeedbackByPage = (items: readonly FeedbackItem[]): readonly FeedbackPageGroup[] => {
	const groups = new Map<string, { host: string; page: string; items: FeedbackItem[] }>()
	const newest = [...items].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
	for (const item of newest) {
		const key = `${item.host}${item.page}`
		const group = groups.get(key) ?? { host: item.host, page: item.page, items: [] }
		group.items.push(item)
		groups.set(key, group)
	}
	return [...groups.values()]
}

export { CORRECTION_STATUSES, FEEDBACK_KIND, FEEDBACK_STATUS, FEEDBACK_STATUSES, feedbackUrl, groupFeedbackByPage }
export type { CorrectionStatus, FeedbackCorrection, FeedbackItem, FeedbackKind, FeedbackNote, FeedbackPageGroup, FeedbackStatus }
